import { COLLABORATOR_SCOPE, SENDOUT_SUPER_USER_ROLES } from 'features/sendouts/common/constants';
import { userHasSomeRoles } from 'services/Authorization';
import { Roles } from 'UI/constants/roles';
import { AdditionalRecruiterStatus, AdditionalRecruiterType } from 'UI/constants/status';

const isApproved = ({ status } = {}) => status === AdditionalRecruiterStatus.Approved;

export const getAccountableTeamFromJobOrder = ({
  recruiter,
  coach,
  additionalRecruiters = []
} = {}) => {
  if (!recruiter) return { accountables: [], collaborators: [] };

  const approved = (additionalRecruiters || []).filter(isApproved);

  const accountables = [
    { id: recruiter.id, coachId: coach?.id ?? null },
    ...approved
      .filter(({ type }) => type === AdditionalRecruiterType.Accountable)
      .map(({ recruiter: entryRecruiter, coach: entryCoach }) => ({
        id: entryRecruiter?.id ?? null,
        coachId: entryCoach?.id ?? null
      }))
  ];

  const collaborators = approved
    .filter(({ type }) => type === AdditionalRecruiterType.Collaborator)
    .map(({ recruiter: entryRecruiter, recruiter_to_collaborate_id: accountableId }) => ({
      id: entryRecruiter?.id ?? null,
      accountableId: accountableId ?? null
    }));

  return { accountables, collaborators };
};

export const getAccountableTeamFromSendoutSide = ({
  accountableId,
  coachId,
  additionalRecruiters = []
} = {}) => ({
  accountables: accountableId ? [{ id: accountableId, coachId: coachId ?? null }] : [],
  collaborators: (additionalRecruiters || [])
    .filter(entry => isApproved(entry) && entry?.type === AdditionalRecruiterType.Collaborator)
    .map(entry => ({
      id: entry?.recruiter?.id ?? null,
      accountableId: entry?.recruiter_to_collaborate_id ?? null
    }))
});

export const isSuperUser = user => userHasSomeRoles(user, SENDOUT_SUPER_USER_ROLES);

export const isUserTeamDirector = ({ accountableTeam = {}, user } = {}) => {
  const { accountables = [] } = accountableTeam;
  const currentUserId = user?.id;
  if (!currentUserId) return false;

  const isTeamCoach = accountables.some(({ coachId }) => coachId === currentUserId);
  const isRegionalDirector =
    userHasSomeRoles(user, [Roles.AssistantRegionalDirector]) &&
    !!user?.teamRelation?.coachId &&
    accountables.some(({ coachId }) => coachId === user.teamRelation.coachId);

  return isTeamCoach || isRegionalDirector;
};

export const isUserInAccountableTeam = ({
  accountableTeam = {},
  user,
  collaboratorScope = COLLABORATOR_SCOPE.PairedToAccountable
} = {}) => {
  const { accountables = [], collaborators = [] } = accountableTeam;
  const currentUserId = user?.id;
  if (!currentUserId) return false;

  const isAccountable = accountables.some(({ id }) => id === currentUserId);

  const isCollaborator =
    collaboratorScope === COLLABORATOR_SCOPE.Any
      ? collaborators.some(({ id }) => id === currentUserId)
      : collaborators.some(
          ({ id, accountableId }) =>
            id === currentUserId && accountables.some(({ id: aId }) => aId === accountableId)
        );

  return isAccountable || isCollaborator || isUserTeamDirector({ accountableTeam, user });
};
